import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  IconButton,
  Collapse
} from '@mui/material';
import {
  RateReview as RevisionIcon,
  Send as SubmitIcon,
  History as HistoryIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Schedule as PendingIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Description as NoteIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { supabase } from '../../supabase-config';

/**
 * RevisionRequest - Technician revision request UI
 *
 * Phase 5.6: Request revisions to completed inspections
 * - Submit revision requests with reason
 * - Track revision status
 * - View revision history
 *
 * @param {number} inspectionId - The inspection to request revision for
 * @param {string} currentStatus - Current inspection status
 * @param {object} inspection - Full inspection data
 * @param {function} onRevisionRequested - Callback when revision is requested
 * @param {boolean} canRequestRevision - Whether user can request
 */
const RevisionRequest = ({
  inspectionId,
  currentStatus,
  inspection,
  onRevisionRequested,
  canRequestRevision = true
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [revisionReason, setRevisionReason] = useState('');
  const [revisionType, setRevisionType] = useState('correction');
  const [priority, setPriority] = useState('medium');
  const [affectedFields, setAffectedFields] = useState([]);

  // Available fields for revision
  const fieldOptions = [
    { value: 'test_results', label: 'Test Results' },
    { value: 'tolerances', label: 'Tolerances' },
    { value: 'scale_info', label: 'Scale Information' },
    { value: 'customer_info', label: 'Customer Information' },
    { value: 'notes', label: 'Notes/Comments' },
    { value: 'photos', label: 'Photos' },
    { value: 'other', label: 'Other' }
  ];

  // Fetch revision history
  const fetchRevisions = useCallback(async () => {
    if (!inspectionId) return;

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('inspection_revisions')
        .select(`
          *,
          requester:users!requested_by(id, email, full_name),
          reviewer:users!reviewed_by(id, email, full_name)
        `)
        .eq('inspection_id', inspectionId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setRevisions(data || []);
    } catch (err) {
      console.error('Failed to fetch revisions:', err);
    } finally {
      setLoading(false);
    }
  }, [inspectionId]);

  useEffect(() => {
    if (historyOpen) {
      fetchRevisions();
    }
  }, [historyOpen, fetchRevisions]);

  // Check if revision can be requested
  const canRequest = canRequestRevision &&
    ['completed', 'approved'].includes(currentStatus) &&
    !revisions.some(r => r.status === 'pending');

  // Submit revision request
  const submitRevision = async () => {
    if (!revisionReason.trim()) {
      setError('Please provide a reason for the revision');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const { data: userData, error: authError } = await supabase.auth.getUser();

      if (authError || !userData?.user?.id) {
        setError('Authentication required. Please log in again.');
        return;
      }
      const userId = userData.user.id;

      // Create revision request
      const { data, error: insertError } = await supabase
        .from('inspection_revisions')
        .insert({
          inspection_id: inspectionId,
          revision_type: revisionType,
          status: 'pending',
          requested_by: userId,
          requested_at: new Date().toISOString(),
          request_reason: revisionReason,
          priority: priority,
          affected_fields: affectedFields,
          original_data: inspection // Store current state for comparison
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Create approval record
      const { data: approvalData, error: approvalError } = await supabase
        .from('inspection_approvals')
        .insert({
          inspection_id: inspectionId,
          revision_id: data.revision_id,
          approval_type: 'revision',
          status: 'pending',
          requested_by: userId,
          requested_at: new Date().toISOString(),
          request_notes: revisionReason,
          request_priority: priority
        })
        .select()
        .single();

      if (approvalError) throw approvalError;

      // Notify managers
      const { data: managers } = await supabase
        .from('users')
        .select('id')
        .in('role', ['admin', 'manager']);

      if (managers && managers.length > 0) {
        const notifications = managers.map((manager) => ({
          approval_id: approvalData.approval_id,
          inspection_id: inspectionId,
          target_user_id: manager.id,
          notification_type: 'revision_requested',
          title: 'Revision Request',
          body: `Revision requested: ${revisionReason.substring(0, 100)}`,
          priority: priority,
          action_url: '/approvals'
        }));

        const { error: notifyError } = await supabase
          .from('approval_notifications')
          .insert(notifications);

        if (notifyError) {
          console.error('Failed to notify managers:', notifyError);
          // Don't fail - revision was created, just log the notification failure
        }
      }

      // Reset form
      setDialogOpen(false);
      setRevisionReason('');
      setRevisionType('correction');
      setPriority('medium');
      setAffectedFields([]);

      if (onRevisionRequested) {
        onRevisionRequested(data);
      }

      // Refresh history
      fetchRevisions();
    } catch (err) {
      console.error('Failed to submit revision:', err);
      setError('Failed to submit revision request');
    } finally {
      setSubmitting(false);
    }
  };

  // Get status chip
  const getStatusChip = (status) => {
    const config = {
      pending: { icon: <PendingIcon />, color: 'warning', label: 'Pending' },
      approved: { icon: <ApprovedIcon />, color: 'success', label: 'Approved' },
      rejected: { icon: <RejectedIcon />, color: 'error', label: 'Rejected' },
      completed: { icon: <ApprovedIcon />, color: 'info', label: 'Completed' }
    };
    const { icon, color, label } = config[status] || config.pending;
    return <Chip icon={icon} label={label} size="small" color={color} />;
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  // Has pending revision
  const hasPendingRevision = revisions.some(r => r.status === 'pending');

  return (
    <Box>
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title={hasPendingRevision ? 'Revision already pending' : 'Request a revision'}>
          <span>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={<RevisionIcon />}
              onClick={() => setDialogOpen(true)}
              disabled={!canRequest}
            >
              Request Revision
            </Button>
          </span>
        </Tooltip>

        {revisions.length > 0 && (
          <Button
            variant="text"
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setHistoryOpen(!historyOpen)}
            endIcon={historyOpen ? <CollapseIcon /> : <ExpandIcon />}
          >
            History ({revisions.length})
          </Button>
        )}
      </Box>

      {/* Pending Revision Alert */}
      {hasPendingRevision && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          A revision request is currently pending approval.
        </Alert>
      )}

      {/* Revision History */}
      <Collapse in={historyOpen}>
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Revision History
          </Typography>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : revisions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No revisions yet
            </Typography>
          ) : (
            <List dense>
              {revisions.map((revision) => (
                <ListItem key={revision.revision_id} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {getStatusChip(revision.status)}
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                      {formatDate(revision.requested_at)}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={revision.revision_type}
                    />
                  </Box>
                  <Box sx={{ mt: 1, pl: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {revision.requester?.full_name || revision.requester?.email}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                      <NoteIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {revision.request_reason}
                      </Typography>
                    </Box>
                    {revision.review_notes && (
                      <Alert
                        severity={revision.status === 'approved' ? 'success' : 'error'}
                        sx={{ mt: 1, py: 0 }}
                      >
                        <Typography variant="caption">
                          {revision.review_notes}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                  <Divider sx={{ width: '100%', mt: 1 }} />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </Collapse>

      {/* Request Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RevisionIcon color="warning" />
            Request Revision
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Revision requests are reviewed by managers before being approved.
              Please provide a clear reason for the revision.
            </Typography>
          </Alert>

          {/* Revision Type */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Revision Type</InputLabel>
            <Select
              value={revisionType}
              label="Revision Type"
              onChange={(e) => setRevisionType(e.target.value)}
            >
              <MenuItem value="correction">Correction - Fix an error</MenuItem>
              <MenuItem value="addition">Addition - Add missing data</MenuItem>
              <MenuItem value="update">Update - Change existing data</MenuItem>
              <MenuItem value="photo">Photo - Add/update photos</MenuItem>
            </Select>
          </FormControl>

          {/* Priority */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priority}
              label="Priority"
              onChange={(e) => setPriority(e.target.value)}
            >
              <MenuItem value="low">Low - No rush</MenuItem>
              <MenuItem value="medium">Medium - Standard</MenuItem>
              <MenuItem value="high">High - Urgent</MenuItem>
            </Select>
          </FormControl>

          {/* Affected Fields */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Affected Fields</InputLabel>
            <Select
              multiple
              value={affectedFields}
              label="Affected Fields"
              onChange={(e) => setAffectedFields(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const option = fieldOptions.find(o => o.value === value);
                    return <Chip key={value} label={option?.label || value} size="small" />;
                  })}
                </Box>
              )}
            >
              {fieldOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Reason */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for Revision"
            value={revisionReason}
            onChange={(e) => setRevisionReason(e.target.value)}
            placeholder="Please explain what needs to be revised and why..."
            required
            helperText="Be specific to help managers approve quickly"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={submitRevision}
            disabled={submitting || !revisionReason.trim()}
            startIcon={submitting ? <CircularProgress size={16} /> : <SubmitIcon />}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * RevisionStatusBadge - Compact badge showing revision status
 */
export const RevisionStatusBadge = ({ status }) => {
  if (!status || status === 'none') return null;

  const config = {
    pending: { color: 'warning', label: 'Revision Pending' },
    approved: { color: 'info', label: 'Revision in Progress' },
    rejected: { color: 'default', label: 'Revision Rejected' },
    completed: { color: 'success', label: 'Revision Complete' }
  };

  const { color, label } = config[status] || config.pending;

  return (
    <Chip
      icon={<RevisionIcon />}
      label={label}
      size="small"
      color={color}
      variant="outlined"
    />
  );
};

/**
 * RevisionTimeline - Visual timeline of revision process
 */
export const RevisionTimeline = ({ revision }) => {
  if (!revision) return null;

  const steps = [
    { label: 'Requested', completed: true, date: revision.requested_at },
    { label: 'Under Review', completed: revision.status !== 'pending' },
    { label: revision.status === 'rejected' ? 'Rejected' : 'Approved', completed: ['approved', 'rejected'].includes(revision.status) },
    { label: 'Completed', completed: revision.status === 'completed' }
  ];

  const activeStep = steps.findIndex(s => !s.completed);

  return (
    <Stepper activeStep={activeStep === -1 ? steps.length : activeStep} alternativeLabel size="small">
      {steps.map((step, index) => (
        <Step key={index} completed={step.completed}>
          <StepLabel
            error={index === 2 && revision.status === 'rejected'}
          >
            {step.label}
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
};

export default RevisionRequest;
